/**
 * Cria uma imagem cropada a partir da área selecionada
 * @param {string} imageSrc - URL da imagem original
 * @param {Object} pixelCrop - Área de crop em pixels {x, y, width, height}
 * @param {number} rotation - Rotação da imagem (opcional)
 * @returns {Promise<Blob>} - Blob da imagem cropada
 */
export async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Falha ao obter contexto do canvas');
  }

  const maxSize = 800; // Tamanho máximo (800x800 como definido no backend)

  // Calcular dimensões do canvas (quadrado)
  const safeArea = Math.max(image.width, image.height) * 2;

  // Configurar canvas para rotação
  canvas.width = safeArea;
  canvas.height = safeArea;

  // Transladar ao centro
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // Desenhar imagem rotacionada
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // Configurar canvas para tamanho final (quadrado)
  const size = Math.min(maxSize, pixelCrop.width);
  canvas.width = size;
  canvas.height = size;

  // Limpar canvas
  ctx.putImageData(data, 0, 0);

  // Desenhar a área cropada
  ctx.globalCompositeOperation = 'destination-over';
  ctx.fillStyle = '#FFFFFF'; // Fundo branco

  // Redimensionar se necessário
  const scale = size / pixelCrop.width;
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    size,
    size
  );

  // Converter canvas para blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas está vazio'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.85 // Qualidade JPEG (85% como definido no backend)
    );
  });
}

/**
 * Cria elemento de imagem a partir de URL
 * @param {string} url - URL da imagem
 * @returns {Promise<HTMLImageElement>}
 */
function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // Para evitar problemas de CORS
    image.src = url;
  });
}

/**
 * Obtém rotação da imagem baseado em EXIF orientation
 * @param {File} file - Arquivo de imagem
 * @returns {Promise<number>} - Rotação em graus
 */
export async function getRotationFromFile(file) {
  const arrayBuffer = await file.arrayBuffer();
  const view = new DataView(arrayBuffer);

  // Verificar se é JPEG
  if (view.getUint16(0, false) !== 0xffd8) {
    return 0;
  }

  const length = view.byteLength;
  let offset = 2;

  while (offset < length) {
    if (view.getUint16(offset + 2, false) <= 8) return 0;
    const marker = view.getUint16(offset, false);
    offset += 2;

    if (marker === 0xffe1) {
      // EXIF marker
      offset += 2;
      if (view.getUint32(offset, false) !== 0x45786966) {
        return 0;
      }

      const little = view.getUint16((offset += 6), false) === 0x4949;
      offset += view.getUint32(offset + 4, little);
      const tags = view.getUint16(offset, little);
      offset += 2;

      for (let i = 0; i < tags; i++) {
        if (view.getUint16(offset + i * 12, little) === 0x0112) {
          const orientation = view.getUint16(offset + i * 12 + 8, little);
          return getRotationFromOrientation(orientation);
        }
      }
    } else if ((marker & 0xff00) !== 0xff00) {
      break;
    } else {
      offset += view.getUint16(offset, false);
    }
  }

  return 0;
}

/**
 * Converte EXIF orientation para rotação em graus
 * @param {number} orientation - EXIF orientation value
 * @returns {number} - Rotação em graus
 */
function getRotationFromOrientation(orientation) {
  switch (orientation) {
    case 3:
      return 180;
    case 6:
      return 90;
    case 8:
      return -90;
    default:
      return 0;
  }
}
